import { useEffect, useState } from 'react';
import { getDocs } from 'firebase/firestore';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Button, Typography, Tooltip, Box } from '@mui/material';
import { Download, Delete, Edit } from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useFirebaseService } from '../../../data/FirebaseService';
import { FirebaseConfig } from '../../../data/firebaseConfig';
import ImportPeople from './ImportPeople';
import { AddPersonDialog, Person } from './AddPersonDialog';
import ConfirmationDialog from '../../../ui/ConfirmationDialog';
import { toast } from 'react-toastify';

const PeoplePage: React.FC = () => {
    const [people, setPeople] = useState<any[]>([]);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [currentPerson, setCurrentPerson] = useState<Person | null>(null);
    const firebaseService = useFirebaseService();

    const functions = getFunctions(FirebaseConfig.app);
    const deletePerson = httpsCallable(functions, "deletePerson");

    useEffect(() => {
        if (firebaseService) {
            const fetchPeople = async () => {
                const peopleCollection = firebaseService.getCollection('people');
                const peopleSnapshot = await getDocs(peopleCollection);
                const peopleList = peopleSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setPeople(peopleList);
            };

            fetchPeople();
        }
    }, [firebaseService]);

    const downloadCSVTemplate = () => {
        const csvTemplate = `firstName,lastName,email,domainName,username,externalId,groups
John,Doe,john.doe@example.com,example.com,jdoe,12345,Group1|Group2
Jane,Smith,jane.smith@company.com,company.com,jsmith,67890,Group1|Group3`;

        const blob = new Blob([csvTemplate], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'recipients-template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePersonAdded = (person: Person) => {
        setPeople([...people, person]);
    };

    const handlePersonUpdated = (updatedPerson: Person) => {
        setPeople(people.map(p => p.id === updatedPerson.id ? updatedPerson : p));
        setOpenEditDialog(false);
        setCurrentPerson(null);
    };

    const handleEdit = (person: Person) => {
        setCurrentPerson(person);
        setOpenEditDialog(true);
    };

    const handleDelete = (person: Person) => {
        setCurrentPerson(person);
        setOpenDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (currentPerson) {
            try {
                await deletePerson({ personId: currentPerson.id });
                setPeople(people.filter(p => p.id !== currentPerson.id));
                toast.success("Person deleted successfully!");
            } catch (error) {
                console.error("Error deleting person:", error);
                toast.error("Failed to delete person. Please try again.");
            }
        }
        setOpenDeleteDialog(false);
        setCurrentPerson(null);
    };

    const handleCloseEditDialog = () => {
        setOpenEditDialog(false);
        setCurrentPerson(null);
    };

    const columns: GridColDef[] = [
        { field: 'firstName', headerName: 'First Name', width: 200 },
        { field: 'lastName', headerName: 'Last Name', width: 200 },
        { field: 'username', headerName: 'Username', width: 200 },
        { field: 'domainName', headerName: 'Domain Name', width: 250 },
        { field: 'email', headerName: 'Email', width: 250 },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 100,
            getActions: (params) => {
                return [
                    <GridActionsCellItem
                        key="edit"
                        label="Edit"
                        onClick={() => handleEdit(params.row)}
                        showInMenu={false}
                        icon={<Edit />}
                    />,
                    <GridActionsCellItem
                        key="delete"
                        label="Delete"
                        onClick={() => handleDelete(params.row)}
                        showInMenu={false}
                        icon={<Delete />}
                    />
                ];
            }
        }
    ];

    function handleClickAddPerson(): void {
        setCurrentPerson(null);
        setOpenEditDialog(true);
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" component="h2" className='page-title'>Recipients</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" color="primary" onClick={handleClickAddPerson}>
                        Add Person
                    </Button>
                    <AddPersonDialog editPerson={currentPerson}
                        open={openEditDialog}
                        onClose={handleCloseEditDialog}
                        onPersonUpdated={handlePersonUpdated}
                        onPersonAdded={handlePersonAdded} />
                    <Tooltip title="Download CSV template with sample data and required column headers">
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<Download />}
                            onClick={downloadCSVTemplate}
                        >
                            Download Template
                        </Button>
                    </Tooltip>
                    <ImportPeople />
                </Box>
            </div>
            <div style={{ height: 'calc(100vh - 160px)', width: '100%' }}>
                <DataGrid rows={people} columns={columns} />
            </div>

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                open={openDeleteDialog}
                title='Confirm Delete'
                content={`Are you sure you want to delete ${currentPerson?.firstName} ${currentPerson?.lastName}?`}
                onConfirm={handleConfirmDelete}
                onCancel={() => setOpenDeleteDialog(false)}
            />
        </div>
    );
};

export default PeoplePage;
